import React, {useCallback, useState} from 'react';
import {twMerge} from 'tailwind-merge';

import {ClassLabelNode, ClassLabels} from '../../util/media';
// eslint-disable-next-line wandb/no-deprecated-imports
import {MaskControl} from '../MediaCard';
import {MaskClass} from './MaskClass';
import {MaskHeader} from './MaskHeader';

type MaskProps = {
  title: string;
  classDict?: ClassLabels;
  maskKey: string;
  maskQueryRegex: RegExp;
  mediaKey: string;
  canShowImage?: boolean;
  getMaskControl: (configPath: Array<string>) => MaskControl | undefined;
  isImageShownForMask?: (mediaKey: string, maskKey: string) => boolean;
  toggleImageForMask?: (mediaKey: string, maskKey: string) => void;
  toggleVisibility?: () => void;
  toggleAllMasksUnder: (
    mediaKey: string,
    maskKey: string,
    classID?: string,
    relevantMasks?: {[key: string]: ClassLabelNode}
  ) => void;
  update: (newControl: Partial<MaskControl>, configPath: Array<string>) => void;
};

const MaskComponent = ({
  title,
  classDict,
  maskKey,
  maskQueryRegex,
  mediaKey,
  canShowImage = true,
  getMaskControl,
  isImageShownForMask,
  toggleImageForMask,
  toggleVisibility,
  toggleAllMasksUnder,
  update,
}: MaskProps) => {
  const onToggleVisibility = useCallback(
    () => toggleAllMasksUnder(mediaKey, maskKey, 'all'),
    [toggleAllMasksUnder, mediaKey, maskKey]
  );

  const onToggleImage = useCallback(() => {
    if (toggleImageForMask) {
      toggleImageForMask(mediaKey, maskKey);
    }
  }, [toggleImageForMask, mediaKey, maskKey]);

  const [isExpanded, setIsExpanded] = useState(true);
  const classes = classDict ?? {};
  const hasClasses = Object.keys(classes).length > 0;

  return (
    <div
      data-test={`mask ${title}`}
      className={twMerge('flex w-full flex-col')}>
      <MaskHeader
        mediaKey={mediaKey}
        maskKey={maskKey}
        title={title}
        onToggleVisibility={toggleVisibility ?? onToggleVisibility}
        canShowImage={canShowImage}
        isImageShownForMask={isImageShownForMask}
        toggleImageForMask={onToggleImage}
        isExpanded={isExpanded}
        setIsExpanded={setIsExpanded}
        hasClasses={hasClasses}
        getMaskControl={getMaskControl}
      />
      {hasClasses && isExpanded && (
        <div
          aria-label={`Mask Classes ${title}`}
          className={twMerge(
            'flex flex-col',
            'py-8',
            'border border-solid border-moon-250'
          )}>
          {Object.entries(classes)
            .filter(([_, name]) => name.match(maskQueryRegex))
            .map(([k, name]) => (
              <MaskClass
                key={mediaKey + maskKey + name + k}
                mediaKey={mediaKey}
                maskKey={maskKey}
                classID={k}
                className={name}
                getMaskControl={getMaskControl}
                update={update}
              />
            ))}
        </div>
      )}
    </div>
  );
};
export const Mask = React.memo(MaskComponent);
